import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import './Dashboard.css';

// ─── Helpers jadwal ─────────────────────────────────────────────────────────
const toTime = (iso) => (iso ? new Date(iso).getTime() : null);

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Intl.DateTimeFormat('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return '-';
  }
};

const formatCountdown = (ms) => {
  if (ms == null || ms <= 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}h ${hours}j ${minutes}m`;
  if (hours > 0) return `${hours}j ${minutes}m ${seconds}d`;
  if (minutes > 0) return `${minutes}m ${seconds}d`;
  return `${seconds} detik`;
};

// module: apa saja yang punya scheduled_start_at / scheduled_end_at / is_available
const getScheduleState = (module, nowMs) => {
  const start = toTime(module?.scheduled_start_at);
  const end = toTime(module?.scheduled_end_at);

  if (!start && !end) return 'no_schedule'; // tidak terjadwal khusus, ikuti aturan lain saja
  if (start && nowMs < start) return 'upcoming';
  if (end && nowMs > end) return 'expired';
  return 'open';
};

const isScheduleOpen = (module, nowMs) => {
  const state = getScheduleState(module, nowMs);
  return state === 'no_schedule' || state === 'open';
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [psychotestStatus, setPsychotestStatus] = useState({
    completed: false,
    result: null,
    module_status: 'pending',
    scheduled_start_at: null,
    scheduled_end_at: null,
    auto_open: 1,
    is_available: true,
    availability_reason: null,
  });
  const [showPsychotestResult, setShowPsychotestResult] = useState(false);
  const [loadingExams, setLoadingExams] = useState(true);
  const [startingId, setStartingId] = useState(null);
  const [error, setError] = useState('');

  // jam "sekarang" yang di-refresh tiap detik supaya status jadwal realtime
  const [nowMs, setNowMs] = useState(Date.now());

  // ─── Fetch daftar ujian ──────────────────────────────────────────────────────
  const fetchExams = useCallback(async () => {
    try {
      const [examRes, psyRes] = await Promise.all([
        API.get('/exam/list'),
        API.get('/psychotest/status'),
      ]);
      setExams(examRes.data);
      setPsychotestStatus({
        completed: !!psyRes.data?.completed,
        result: psyRes.data?.result || null,
        module_status: psyRes.data?.module_status || 'pending',
        scheduled_start_at: psyRes.data?.scheduled_start_at || null,
        scheduled_end_at: psyRes.data?.scheduled_end_at || null,
        auto_open: psyRes.data?.auto_open == null ? 1 : Number(psyRes.data.auto_open),
        is_available: psyRes.data?.is_available == null ? true : !!psyRes.data.is_available,
        availability_reason: psyRes.data?.availability_reason || null,
      });
      setShowPsychotestResult(false);
    } catch (err) {
      setError('Gagal memuat daftar ujian');
    } finally {
      setLoadingExams(false);
    }
  }, []);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  // ─── Jam realtime (tick tiap 1 detik) ────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Refetch dari server tiap 60 detik sebagai fallback, kalau-kalau koneksi
  // realtime (SSE) sempat putus tanpa reconnect berhasil.
  useEffect(() => {
    const interval = setInterval(fetchExams, 60000);
    return () => clearInterval(interval);
  }, [fetchExams]);

  // Refetch langsung begitu tab/browser kandidat kembali aktif.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchExams();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [fetchExams]);

  // ─── Realtime: dengarkan perubahan jadwal langsung dari server (SSE) ───────
  // Begitu admin simpan jadwal, server push event ini dan dashboard langsung
  // refetch tanpa menunggu interval polling di atas.
  useEffect(() => {
    const token = localStorage.getItem('token'); // sesuaikan dengan cara API.js menyimpan token
    if (!token) return;

    const apiBase = String(process.env.REACT_APP_API_URL || '/api').replace(/\/?$/, '');
    const eventSource = new EventSource(`${apiBase}/candidate/schedule-events?token=${encodeURIComponent(token)}`);

    eventSource.addEventListener('schedule-updated', () => {
      fetchExams();
    });

    eventSource.onerror = () => {
      // EventSource akan retry otomatis; kalau gagal terus, polling 60 detik
      // di atas tetap jadi jaring pengaman.
    };

    return () => eventSource.close();
  }, [fetchExams]);

  // ─── Auto-reload begitu jadwal mulai terdekat tercapai ──────────────────────
  // Cari scheduled_start_at paling dekat (dari psikotest maupun tiap exam) yang
  // masih di masa depan, lalu pasang timer supaya halaman otomatis reload tepat
  // saat waktunya tiba — biar kandidat tidak perlu refresh manual.
  useEffect(() => {
    const upcomingStarts = [
      psychotestStatus.scheduled_start_at,
      ...exams.map((exam) => exam.scheduled_start_at),
    ]
      .map(toTime)
      .filter((t) => t && t > Date.now());

    if (upcomingStarts.length === 0) return undefined;

    const nextStart = Math.min(...upcomingStarts);
    const delay = nextStart - Date.now();

    // setTimeout punya batas maksimum (~24.8 hari); kalau jadwalnya lebih jauh
    // dari itu, biarkan saja — nanti akan ke-refresh lewat SSE/polling/tab-focus.
    if (delay <= 0 || delay > 2147483647) return undefined;

    const timeout = setTimeout(() => {
      window.location.reload();
    }, delay + 250); // +250ms jaga-jaga selisih clock klien vs server

    return () => clearTimeout(timeout);
  }, [exams, psychotestStatus]);

  // ─── Cek apakah ujian/psikotest boleh dikerjakan ─────────────────────────────
  // Aturan: dikunci oleh jadwal + ketersediaan dari backend, DITAMBAH aturan baru:
  // kalau ada modul sebelumnya (urutan berdasarkan jadwal) yang gagal/diskualifikasi,
  // modul berikutnya ikut terkunci apa pun jadwalnya sendiri.
const isExamUnlocked = (exam, blockedByPrevious) => {
    if (blockedByPrevious) return false;
    if (exam.is_available === false) return false;
    if (!isScheduleOpen(exam, nowMs)) return false;
    return true;
};

  const isPsychotestUnlocked = (blockedByPreviousFailure) => {
    if (blockedByPreviousFailure) return false;
    if (psychotestStatus.module_status === 'disqualified') return false;
    if (psychotestStatus.is_available === false) return false;
    if (!isScheduleOpen(psychotestStatus, nowMs)) return false;
    return true;
  };

  // Modul dianggap "gagal" kalau statusnya failed/disqualified.
  const isModuleFailed = (m) => {
    if (m.type === 'psychotest') {
      return ['failed', 'disqualified'].includes(psychotestStatus.module_status);
    }
    return ['failed', 'disqualified'].includes(m.exam.session_status);
  };

  // ─── Urutan tampilan modul (psikotest vs tiap exam) berdasarkan jadwal ──────
  // Modul dengan scheduled_start_at paling awal tampil paling atas. Modul yang
  // tidak punya jadwal khusus (null) dianggap "kapan saja" dan diletakkan di
  // urutan paling awal juga, dengan urutan asli (psikotest, lalu exam sesuai
  // urutan dari backend) sebagai tie-breaker.
  const getOrderedModules = () => {
    const modules = [
      {
        type: 'psychotest',
        key: 'psychotest',
        scheduled_start_at: psychotestStatus.scheduled_start_at,
      },
      ...exams.map((exam) => ({
        type: 'exam',
        key: exam.id,
        scheduled_start_at: exam.scheduled_start_at,
        exam,
      })),
    ];

    return modules
      .map((m, idx) => ({
        ...m,
        _sortTime: m.scheduled_start_at ? toTime(m.scheduled_start_at) : -Infinity,
        _idx: idx,
      }))
      .sort((a, b) => a._sortTime - b._sortTime || a._idx - b._idx);
  };

  // ─── Mulai / lanjut ujian ────────────────────────────────────────────────────
  const startExam = useCallback(
    async (exam_id) => {
      setStartingId(exam_id);
      setError('');
      try {
        const res = await API.post('/exam/start', { exam_id });
        navigate(`/exam/${res.data.session_id}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Gagal memulai ujian');
        setStartingId(null);
      }
    },
    [navigate]
  );

  // ─── Format durasi ────────────────────────────────────────────────────────────
  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes} Menit`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h} Jam ${m} Menit` : `${h} Jam`;
  };

  // ─── Status badge ujian ───────────────────────────────────────────────────────
  const getStatusBadge = (status) => {
    if (!status) return null;
    const map = {
      active: { label: '▶ Sedang Berlangsung', cls: 'badge-active' },
      finished: { label: '✅ Selesai', cls: 'badge-finished' },
      failed: { label: '❌ Tidak Lulus', cls: 'badge-failed' },
      disqualified: { label: '🚫 Didiskualifikasi', cls: 'badge-disq' },
    };
    const s = map[status];
    if (!s) return null;
    return <span className={`exam-badge ${s.cls}`}>{s.label}</span>;
  };

  // ─── Badge & teks jadwal (dipakai untuk psikotest maupun exam) ───────────────
  const renderScheduleInfo = (module) => {
    const state = getScheduleState(module, nowMs);
    if (state === 'no_schedule') return null;

    const start = toTime(module.scheduled_start_at);
    const end = toTime(module.scheduled_end_at);

    if (state === 'upcoming') {
      return (
        <div className="schedule-box">
          <div className="schedule-row">
            <span className="info-label">🕐 Jadwal Mulai</span>
            <span className="info-val">{formatDateTime(module.scheduled_start_at)}</span>
          </div>
          {module.scheduled_end_at && (
            <div className="schedule-row">
              <span className="info-label">🏁 Jadwal Selesai</span>
              <span className="info-val">{formatDateTime(module.scheduled_end_at)}</span>
            </div>
          )}
          <div className="countdown-text">
            ⏳ Dibuka dalam {formatCountdown(start - nowMs)}
          </div>
        </div>
      );
    }

    if (state === 'expired') {
      return (
        <div className="schedule-box schedule-expired">
          <span className="info-label">⛔ Waktu telah berakhir</span>
          <span className="info-val">
            Jadwal ditutup pada {formatDateTime(module.scheduled_end_at)}
          </span>
        </div>
      );
    }

    // state === 'open'
    return (
      <div className="schedule-box schedule-open">
        <span className="info-label">🟢 Sedang dibuka</span>
        {module.scheduled_end_at && (
          <span className="info-val">
            Ditutup {formatDateTime(module.scheduled_end_at)} (
            {formatCountdown(end - nowMs)} lagi)
          </span>
        )}
      </div>
    );
  };

  // ─── Label tombol ─────────────────────────────────────────────────────────────
  const getButtonLabel = (
    exam,
    isStarting,
    blockedByPreviousFailure,
    blockedByPreviousNotFinished
) => {

    if (isStarting) return 'Memuat...';

    if (blockedByPreviousFailure)
        return '🚫 Modul Sebelumnya Gagal';

    if (blockedByPreviousNotFinished)
        return '⏳ Selesaikan Ujian Sebelumnya';
    const scheduleState = getScheduleState(exam, nowMs);
    if (scheduleState === 'upcoming') return '🕐 Belum Waktunya';
    if (scheduleState === 'expired') return '⛔ Waktu Sudah Berakhir';

    if (exam.session_status === 'active') return '▶ Lanjutkan Ujian';
    if (exam.session_status === 'finished' || exam.session_status === 'failed') {
      return '📄 Lihat Hasil';
    }
    if (exam.session_status === 'disqualified') return '🚫 Didiskualifikasi';
    return '🚀 Mulai Ujian';
  };

  const handleButton = (exam, blockedByPreviousFailure) => {
    if (!isExamUnlocked(exam, blockedByPreviousFailure)) return;

    if (['finished', 'failed', 'disqualified'].includes(exam.session_status)) {
      navigate(`/result/${exam.session_id}`);
      return;
    }
    startExam(exam.id);
  };

 const orderedModulesRaw = getOrderedModules();

let previousLockReason = null;

const orderedModules = orderedModulesRaw.map((m) => {

    const blockedByPreviousFailure =
        previousLockReason === 'failed';

    const blockedByPreviousNotFinished =
        previousLockReason === 'not_finished';

    // Tentukan status modul saat ini
    if (m.type === 'psychotest') {

        if (!psychotestStatus.completed) {
            previousLockReason = 'not_finished';
        }

    } else {

        const status = m.exam.session_status;

        if (status === 'failed' || status === 'disqualified') {
            previousLockReason = 'failed';
        }
        else if (status !== 'finished') {
            previousLockReason = 'not_finished';
        }

    }

    return {
        ...m,
        blockedByPreviousFailure,
        blockedByPreviousNotFinished
    };
});

  // ─── Render kartu psikotest ───────────────────────────────────────────────────
  const renderPsychotestCard = (blockedByPreviousFailure) => {
    const disqualified = psychotestStatus.module_status === 'disqualified';
    const psychotestUnlocked = isPsychotestUnlocked(blockedByPreviousFailure);
    const isLocked = disqualified || blockedByPreviousFailure || (!psychotestStatus.completed && !psychotestUnlocked);

    return (
      <div
        className={`exam-card psychotest-card ${
          psychotestStatus.completed ? 'psychotest-completed' : ''
        } ${isLocked ? 'exam-card-locked' : ''}`}
        key="psychotest"
      >
        {disqualified && (
          <div className="lock-overlay">
            <span className="lock-icon">🚫</span>
            <p>{psychotestStatus.result?.reason || 'Anda didiskualifikasi dari psikotest'}</p>
          </div>
        )}

        {!disqualified && blockedByPreviousFailure && !psychotestStatus.completed && (
          <div className="lock-overlay">
            <span className="lock-icon">🚫</span>
            <p>Modul sebelumnya tidak lulus/didiskualifikasi, sehingga psikotest ini tidak dapat dikerjakan.</p>
          </div>
        )}

        {!disqualified && !blockedByPreviousFailure && !psychotestStatus.completed && !psychotestUnlocked && (
          <div className="lock-overlay">
            <span className="lock-icon">
              {getScheduleState(psychotestStatus, nowMs) === 'expired' ? '⛔' : '🕐'}
            </span>
            <p>
              {getScheduleState(psychotestStatus, nowMs) === 'expired'
                ? 'Waktu psikotest telah berakhir'
                : 'Menunggu jadwal psikotest dibuka'}
            </p>
          </div>
        )}

        <div className="exam-card-header">
          <div className="exam-icon">{disqualified ? '🚫' : '🧠'}</div>
          <div className="exam-card-title">
            <h2>Psikotest Pra-Exam</h2>
            <span
              className={`exam-badge ${
                disqualified
                  ? 'badge-disq'
                  : psychotestStatus.completed
                  ? 'badge-finished'
                  : 'badge-active'
              }`}
            >
              {disqualified
                ? '🚫 Didiskualifikasi'
                : psychotestStatus.completed
                ? '✅ Selesai'
                : '⏳ Wajib Dikerjakan'}
            </span>
          </div>
        </div>

        <p className="exam-desc">
          {disqualified
            ? 'Anda didiskualifikasi dari psikotest karena melanggar aturan pengerjaan.'
            : blockedByPreviousFailure
            ? 'Modul sebelumnya tidak lulus/didiskualifikasi.'
            : psychotestStatus.completed
            ? 'Psikotest sudah selesai dan hasil sudah direkam.'
            : 'Kerjakan psikotest sesuai jadwal yang tersedia.'}
        </p>

        {!disqualified && !blockedByPreviousFailure && !psychotestStatus.completed && renderScheduleInfo(psychotestStatus)}

        <div className="exam-info">
          <div className="info-item">
            <span className="info-label">📌 Status</span>
            <span className="info-val">
              {disqualified
                ? 'Didiskualifikasi'
                : psychotestStatus.completed
                ? 'Selesai'
                : 'Belum Selesai'}
            </span>
          </div>
        </div>

        {!disqualified &&
          psychotestStatus.completed &&
          showPsychotestResult &&
          (psychotestStatus.result?.dimensions || []).length > 0 && (
            <div className="rules-box">
              <h3>📊 Ringkasan Hasil Psikotest</h3>
              <ul>
                {(psychotestStatus.result?.dimensions || []).map((d) => (
                  <li key={d.key}>
                    {d.title}: {d.tendency} ({d.score}%)
                  </li>
                ))}
              </ul>
            </div>
          )}

        {!disqualified &&
          psychotestStatus.completed &&
          showPsychotestResult &&
          (psychotestStatus.result?.dimensions || []).length === 0 && (
            <div className="rules-box">
              <h3>📊 Ringkasan Hasil Psikotest</h3>
              <p>Hasil psikotest belum tersedia.</p>
            </div>
          )}

        <button
          className={`btn-start ${
            psychotestStatus.completed ? 'btn-secondary-start' : ''
          } ${disqualified || blockedByPreviousFailure ? 'btn-disq' : ''} ${
            !disqualified && !blockedByPreviousFailure && !psychotestUnlocked && !psychotestStatus.completed
              ? 'btn-locked'
              : ''
          }`}
          style={{ maxWidth: '360px' }}
          disabled={
            disqualified || blockedByPreviousFailure || (!psychotestStatus.completed && !psychotestUnlocked)
          }
          onClick={() => {
            if (disqualified || blockedByPreviousFailure) return;
            if (psychotestStatus.completed) {
              setShowPsychotestResult((prev) => !prev);
            } else {
              navigate('/psychotest');
            }
          }}
        >
          {disqualified
            ? '🚫 Didiskualifikasi'
            : blockedByPreviousFailure
            ? '🚫 Modul Sebelumnya Gagal'
            : psychotestStatus.completed
            ? showPsychotestResult
              ? 'Sembunyikan Hasil Psikotest'
              : 'Lihat Hasil Psikotest'
            : psychotestUnlocked
            ? 'Mulai Psikotest'
            : getScheduleState(psychotestStatus, nowMs) === 'expired'
            ? '⛔ Waktu Sudah Berakhir'
            : '🕐 Belum Waktunya'}
        </button>
      </div>
    );
  };

  // ─── Render kartu exam ─────────────────────────────────────────────────────────
  const renderExamCard = (
    exam,
    blockedByPreviousFailure,
    blockedByPreviousNotFinished
) => {
    const unlocked = isExamUnlocked(
        exam,
        blockedByPreviousFailure || blockedByPreviousNotFinished
    );
    const isStarting = startingId === exam.id;
    const isBlocked = !unlocked;
    const scheduleState = getScheduleState(exam, nowMs);

    let lockMessage = null;
    if (blockedByPreviousFailure) {

    lockMessage =
        'Modul sebelumnya tidak lulus atau didiskualifikasi, sehingga ujian ini tidak dapat dikerjakan.';

}
else if (blockedByPreviousNotFinished) {

    lockMessage =
        'Selesaikan ujian sebelumnya terlebih dahulu.';

} else if (scheduleState === 'upcoming') {
      lockMessage = `Dibuka pada ${formatDateTime(exam.scheduled_start_at)}`;
    } else if (scheduleState === 'expired') {
      lockMessage = 'Waktu ujian ini telah berakhir';
    } else if (exam.is_available === false) {
      lockMessage = exam.availability_reason || 'Ujian ini belum tersedia';
    }

    return (
      <div className={`exam-card ${isBlocked ? 'exam-card-locked' : ''}`} key={exam.id}>
        {isBlocked && (
          <div className="lock-overlay">
            <span className="lock-icon">
              {blockedByPreviousFailure
                ? '🚫'
                : scheduleState === 'expired'
                ? '⛔'
                : scheduleState === 'upcoming'
                ? '🕐'
                : '🔒'}
            </span>
            <p>{lockMessage}</p>
          </div>
        )}

        <div className="exam-card-header">
          <div className="exam-icon">{isBlocked ? '🔒' : '📝'}</div>
          <div className="exam-card-title">
            <h2>{exam.title}</h2>
            {getStatusBadge(exam.session_status)}
          </div>
        </div>

        {exam.description && <p className="exam-desc">{exam.description}</p>}

        {!blockedByPreviousFailure && renderScheduleInfo(exam)}

        <div className="exam-info">
          <div className="info-item">
            <span className="info-label">⏱ Durasi</span>
            <span className="info-val">{formatDuration(exam.duration_minutes)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">❓ Soal</span>
            <span className="info-val">{exam.total_questions} Pertanyaan</span>
          </div>
          <div className="info-item">
            <span className="info-label">⚠️ Maks. Pelanggaran</span>
            <span className="info-val">{exam.max_violations} Kali</span>
          </div>
        </div>

        <div className="rules-box">
          <h3>⚠️ Peraturan Ujian</h3>
          <ul>
            <li>Ujian wajib dilakukan dalam mode layar penuh</li>
            <li>Dilarang berpindah tab atau keluar jendela browser</li>
            <li>Kamera wajah harus aktif selama ujian</li>
            <li>Wajah harus terdeteksi — jika hilang &gt;5 detik = pelanggaran</li>
            <li>Lebih dari <strong>{exam.max_violations} pelanggaran</strong> akan didiskualifikasi</li>
            <li>Copy-paste dinonaktifkan selama ujian</li>
            <li>Jawaban tersimpan otomatis</li>
          </ul>
        </div>

        <button
          className={`btn-start ${!unlocked ? 'btn-locked' : ''} ${
            exam.session_status === 'disqualified' || blockedByPreviousFailure ? 'btn-disq' : ''
          }`}
          onClick={() =>
              handleButton(
                  exam,
                  blockedByPreviousFailure || blockedByPreviousNotFinished
              )
          }
          disabled={
    isStarting ||
    !unlocked ||
    exam.session_status === 'disqualified' ||
    blockedByPreviousFailure ||
    blockedByPreviousNotFinished
}
        >
          {getButtonLabel(
    exam,
    isStarting,
    blockedByPreviousFailure,
    blockedByPreviousNotFinished
)}
        </button>
      </div>
    );
  };

  return (
    <div className="dashboard">
      <header className="dash-header">
        <div className="dash-logo">📋 Sistem Ujian Rekrutmen</div>
        <div className="dash-user">
          <span>👤 {user?.name}</span>
          <button onClick={logout} className="btn-logout">
            Keluar
          </button>
        </div>
      </header>

      <main className="dash-main">
        <div className="welcome-card">
          <h1>Selamat Datang, {user?.name}!</h1>
          <p>Anda terdaftar sebagai peserta ujian rekrutmen.</p>
        </div>

        {error && <div className="alert-error">{error}</div>}

        {loadingExams ? (
          <div className="loading-screen">
            <div className="spinner"></div>
            <p>Memuat daftar ujian...</p>
          </div>
        ) : exams.length === 0 ? (
          <div className="empty-state">
            <p>Tidak ada ujian yang tersedia saat ini.</p>
          </div>
        ) : (
          <div className="exam-list">
            {orderedModules.map((m) =>
              m.type === 'psychotest'
                ? renderPsychotestCard(m.blockedByPreviousFailure)
                : renderExamCard(
    m.exam,
    m.blockedByPreviousFailure,
    m.blockedByPreviousNotFinished
)
            )}
          </div>
        )}
      </main>
    </div>
  );
}